import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { effectiveParams } from '../params.js';
import { getDisplayUnits } from '../units.js';
import { endSentence, readSalinityUnit } from '../salinity.js';
import { toDisplayValue, displayUnit } from '../all-history.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { confirmPromptFor } from '../reading-confirm.js';
import { daysAgoText, farOutRows, newestRecent, rowsToImport } from '../import-review.js';
import {
  IMPORT_MAX_ROWS,
  UNIT_OPTIONS,
  buildRows,
  detectDateOrder,
  findHeaderRow,
  matchParam,
  parseDateCell,
  parseDelimited,
  parseNumber,
  suggestColumns,
  suggestUnit,
  widen,
  withoutWeekday,
} from '../../../shared/readings-import.js';
import './treatments.css';
import './import-readings.css';

// Past readings from the keeper's own spreadsheet (feedback #67). A page,
// not a sheet: choose a file or paste, match the columns, check, import.
// Every unit is the keeper's to confirm; the server checks every row again.

const FILE_TYPES = '.csv,.tsv,.txt,.xlsx,text/csv,text/plain';

const fmtDay = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

function mimeFor(file) {
  if (/\.xlsx$/i.test(file.name || '')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  return 'text/csv';
}

export function ImportReadingsView({ tank, onClose, onOpenAllHistory, onReload }) {
  const confirm = useConfirm();
  // Columns map only to the cards on Home, the ones the picker lists. A
  // column naming a hidden card is skipped and says so; the full map only
  // finds the heading row.
  const params = useMemo(() => effectiveParams(tank), [tank]);
  const visibleMap = useMemo(() => Object.fromEntries(params.map((p) => [p.key, p])), [params]);
  const paramMap = useMemo(
    () => Object.fromEntries(effectiveParams(tank, { includeHidden: true }).map((p) => [p.key, p])),
    [tank]
  );
  const hiddenMap = useMemo(
    () => Object.fromEntries(Object.entries(paramMap).filter(([, p]) => p.hidden)),
    [paramMap]
  );
  const opts = useMemo(
    () => ({ salinityUnit: readSalinityUnit(), tempUnit: getDisplayUnits(tank).temp }),
    [tank]
  );
  const [stage, setStage] = useState('pick');
  const [paste, setPaste] = useState('');
  const [fileName, setFileName] = useState('');
  const [table, setTable] = useState(null);
  const [headerLine, setHeaderLine] = useState(1);
  // An Excel file: kept so another sheet can be read without choosing the
  // file again; its sheet names, the one read, and its date system.
  const [book, setBook] = useState(null);
  const [columns, setColumns] = useState([]);
  const [dateOrder, setDateOrder] = useState(null);
  const [check, setCheck] = useState(null);
  // The keeper's "they are right" is for the numbers they saw: any change
  // to the columns, units or date order asks again.
  const [includeOdd, setIncludeOdd] = useState(false);
  // Kinds of test saved as a test on this visit, never offered twice.
  const [savedParams, setSavedParams] = useState(() => new Set());
  const [result, setResult] = useState(null);
  const [imports, setImports] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // The page opens where Home was scrolled to; start at its top, and bring
  // an error into view on a phone (review, 2026-10-08).
  const topRef = useRef(null);
  const errRef = useRef(null);
  useEffect(() => {
    topRef.current?.scrollIntoView?.({ block: 'start' });
  }, []);
  useEffect(() => {
    if (err) errRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [err]);

  const loadImports = () =>
    api(`/api/tanks/${tank.id}/readings/imports`)
      .then((r) => setImports(r?.imports || []))
      .catch(() => setImports([]));
  useEffect(() => {
    loadImports();
  }, [tank.id]);

  const dateCol = columns.findIndex((c) => c.kind === 'date');
  const detectedOrder = useMemo(() => {
    if (!table || dateCol < 0) return null;
    return detectDateOrder(table.slice(1).map((r) => r[dateCol]));
  }, [table, dateCol]);
  const order =
    detectedOrder === 'dmy' || detectedOrder === 'mdy'
      ? detectedOrder
      : detectedOrder === 'none'
        ? 'mdy'
        : dateOrder;
  const sampleDate = useMemo(() => {
    if (!table || dateCol < 0) return '';
    return (
      table
        .slice(1)
        .map((r) => withoutWeekday(r[dateCol]))
        .find((c) => /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}/.test(c)) || ''
    );
  }, [table, dateCol]);

  const built = useMemo(() => {
    if (!table || !order) return null;
    return buildRows(table, columns, {
      params: visibleMap,
      subtypeSlug: tank.subtype_slug,
      dateOrder: order,
      headerLine,
      date1904: !!book?.date1904,
    });
  }, [table, columns, order, visibleMap, tank.subtype_slug, headerLine, book]);

  // Numbers far outside the keeper's range stay out unless they say so
  // (import-review.js).
  const odd = useMemo(
    () => (built ? farOutRows(built.rows, tank, visibleMap) : []),
    [built, tank, visibleMap]
  );
  const rowsToSend = (include) => rowsToImport(built?.rows, odd, include);

  const startMapping = (rows, name) => {
    const h = findHeaderRow(rows, paramMap);
    setHeaderLine(h + 1);
    const wide = widen(rows.slice(h), { headerLine: h + 1 });
    setTable(wide);
    setColumns(suggestColumns(wide, visibleMap));
    setDateOrder(null);
    setIncludeOdd(false);
    setSavedParams(new Set());
    setFileName(name);
    setCheck(null);
    setStage('map');
  };

  const readFile = async (file, sheet = null) => {
    if (!file) return;
    setErr('');
    setBusy(true);
    try {
      const qs = sheet != null ? `?sheet=${sheet}` : '';
      const r = await fetch(`/api/tanks/${tank.id}/readings/import/read${qs}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': mimeFor(file) },
        body: file,
      });
      const body = await r.json().catch(() => ({}));
      const sheets = Array.isArray(body.sheets) ? body.sheets : null;
      setBook(
        sheets
          ? {
              file,
              sheets,
              hidden: Array.isArray(body.hidden) ? body.hidden : [],
              sheet: body.sheet ?? 0,
              date1904: !!body.date1904,
            }
          : null
      );
      if (!r.ok) throw new Error(body.error || `That file could not be read (${r.status}).`);
      startMapping(body.rows || [], file.name || 'spreadsheet');
    } catch (e) {
      // Never keep the last sheet's table after a failed read: it would be
      // checked under another sheet's date system (review, 2026-10-08).
      setTable(null);
      setColumns([]);
      setCheck(null);
      setStage('pick');
      setErr(e.message || 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const readPaste = () => {
    setErr('');
    const rows = parseDelimited(paste);
    const filled = rows.filter((r) => r.some((c) => String(c ?? '').trim() !== ''));
    if (filled.length < 2) {
      setErr('Paste a heading row and at least one row of readings.');
      return;
    }
    setBook(null);
    startMapping(rows, 'pasted readings');
  };

  // Another sheet of the same workbook, read again from the kept file.
  const sheetPicker =
    book && book.sheets.filter((_n, i) => !book.hidden?.[i]).length > 1 ? (
      <label className="ir-field">
        <span className="ir-label font-heading">Sheet to read</span>
        <select
          value={book.sheet}
          disabled={busy}
          onChange={(e) => readFile(book.file, Number(e.target.value))}
        >
          {/* A hidden sheet keeps its own index but is not offered. */}
          {book.sheets.map((name, i) =>
            book.hidden?.[i] && i !== book.sheet ? null : (
              <option key={`${i}-${name}`} value={i}>
                {name}
              </option>
            )
          )}
        </select>
      </label>
    ) : null;

  const setColumn = (i, value) => {
    setCheck(null);
    setIncludeOdd(false);
    setColumns((cols) =>
      cols.map((c, j) => {
        if (j !== i) {
          // One date column and one time column at most.
          if (
            (value === '__date' && c.kind === 'date') ||
            (value === '__time' && c.kind === 'time')
          )
            return { ...c, kind: 'skip', param: undefined, unit: undefined };
          return c;
        }
        if (value === '__date') return { header: c.header, kind: 'date' };
        if (value === '__time') return { header: c.header, kind: 'time' };
        if (value === '__skip') return { header: c.header, kind: 'skip' };
        const values = table.slice(1).map((r) => r[i]);
        return {
          header: c.header,
          kind: 'param',
          param: value,
          unit: suggestUnit(value, c.header, values),
        };
      })
    );
  };

  const setUnit = (i, unit) => {
    setCheck(null);
    setIncludeOdd(false);
    setColumns((cols) => cols.map((c, j) => (j === i ? { ...c, unit } : c)));
  };

  const runCheck = async (include = includeOdd) => {
    setErr('');
    if (dateCol < 0) return setErr('Choose which column holds the date.');
    if (!columns.some((c) => c.kind === 'param')) {
      return setErr('Choose at least one column of readings.');
    }
    if (!order) return setErr('Say which way the dates read first.');
    const rows = rowsToSend(include);
    if (!rows.length) {
      setCheck({ ready: 0, refused: [], duplicates: 0 });
      setStage('check');
      return true;
    }
    if (rows.length > IMPORT_MAX_ROWS) {
      setErr(
        `That is ${rows.length} readings. One import takes up to ${IMPORT_MAX_ROWS}: split the sheet and import it in parts.`
      );
      return false;
    }
    setBusy(true);
    try {
      const r = await api(`/api/tanks/${tank.id}/readings/import`, {
        method: 'POST',
        body: { rows, file: fileName, dry_run: true },
      });
      setCheck(r);
      setStage('check');
      return true;
    } catch (e) {
      setErr(e.message || 'The check did not go through.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    setErr('');
    setBusy(true);
    try {
      const r = await api(`/api/tanks/${tank.id}/readings/import`, {
        method: 'POST',
        body: { rows: rowsToSend(includeOdd), file: fileName, dry_run: false },
      });
      setResult(r);
      setStage('done');
      loadImports();
      onReload?.();
    } catch (e) {
      setErr(e.message || 'The import did not go through.');
      loadImports();
    } finally {
      setBusy(false);
    }
  };

  // A recent test is not history: it is saved as a test with its own date
  // through the normal reading route, which checks it like one logged by
  // hand (and may alert). The older recent rows then pass as history.
  const saveAsTest = async (r) => {
    const ask = confirmPromptFor(tank, r.param, r.value, { salinityUnit: opts.salinityUnit });
    if (ask) {
      const ok = await confirm({ title: 'Check this number', body: ask, confirmLabel: 'Save it' });
      if (!ok) return;
    }
    setErr('');
    setBusy(true);
    try {
      await api(`/api/tanks/${tank.id}/readings`, {
        method: 'POST',
        body: {
          param: r.param,
          value: r.value,
          unit: visibleMap[r.param]?.unit || null,
          method: 'manual',
          ts: new Date(Math.min(Date.parse(r.ts), Date.now())).toISOString(),
        },
      });
      setSavedParams((prev) => new Set(prev).add(r.param));
      onReload?.();
      // The old count no longer holds once a test is saved: if the re-check
      // fails, go back a step rather than offer it.
      if (!(await runCheck(includeOdd))) {
        setCheck(null);
        setStage('map');
        setErr(
          `Your ${label(r.param).toLowerCase()} test is saved. The check did not go through: tap Check the readings again.`
        );
      }
    } catch (e) {
      setErr(e.message || 'That test could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const removeImport = async (imp) => {
    const ok = await confirm({
      title: 'Remove this import?',
      body: `The ${imp.count} reading${imp.count === 1 ? '' : 's'} from ${imp.file} come off this tank's history. Readings logged any other way stay.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/readings/import/${imp.import_id}`, { method: 'DELETE' });
      if (result?.import_id === imp.import_id) {
        setResult(null);
        setStage('pick');
      }
      loadImports();
      onReload?.();
    } catch (e) {
      setErr(e.message || 'That import could not be removed.');
    }
  };

  const reset = () => {
    setBook(null);
    setStage('pick');
    setTable(null);
    setColumns([]);
    setCheck(null);
    setResult(null);
    setPaste('');
    setErr('');
  };

  const label = (k) => paramMap[k]?.label || k;
  // "3.1 meq/L is 8.68 dKH": the first number of the column, converted, so
  // a wrong unit shows before anything is checked.
  const unitPreview = (c, samples) => {
    const opt = (UNIT_OPTIONS[c.param] || []).find((o) => o.k === c.unit);
    if (!opt || (opt === UNIT_OPTIONS[c.param][0] && c.param !== 'salinity')) return null;
    const raw = samples.map((v) => parseNumber(v)).find((v) => v != null);
    if (raw == null) return null;
    const stored = opt.to(raw);
    // "8.2 is 8.2 dKH" says nothing: only a number that changes is shown.
    if (String(shownNumber(c.param, stored)) === String(raw)) return null;
    const unitWord = opt.label.split(' (')[0];
    const tiny =
      c.param === 'phosphate' && raw > 0 && stored < 0.001
        ? ' That is smaller than a test kit shows: check the unit.'
        : '';
    return `${raw} ${c.param === 'salinity' ? '' : unitWord + ' '}is ${show(c.param, stored)}.${tiny}`.replace(
      /\s+/g,
      ' '
    );
  };
  // An S.G. keeps its three places (1.010, never 1.01), and a phosphate
  // four, so a wrong unit never prints as a believable 0 ppm.
  const shownNumber = (k, v) => {
    if (k === 'phosphate' && Number.isFinite(Number(v))) {
      return Math.round(Number(v) * 10000) / 10000;
    }
    const d = toDisplayValue(k, v, opts);
    return k === 'salinity' && opts.salinityUnit === 'sg' && d != null ? d.toFixed(3) : d;
  };
  const show = (k, v) => `${shownNumber(k, v)} ${displayUnit(k, paramMap[k]?.unit, opts)}`.trim();

  const refusedAll = [...(built?.refused || []), ...(check?.refused || [])];
  const recentNewest = newestRecent(check?.refused, savedParams);
  const whenTested = (r) =>
    `${
      r.date_only
        ? fmtDay(r.ts)
        : `${fmtDay(r.ts)}, ${new Date(r.ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    } (${daysAgoText(r.ts)})`;
  // The server's count per test, after duplicates and recent tests are left
  // out, so the lines add up to the total.
  const perParam = check?.by_param || {};

  return (
    <div className="ir" style={{ padding: '0 16px 32px' }}>
      <header
        ref={topRef}
        style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 12px' }}
      >
        <button type="button" className="ph-back" onClick={onClose} aria-label="Back">
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ph-eyebrow">{tank.name}</div>
          <h2 className="font-heading ph-title">Import past readings</h2>
        </div>
      </header>

      {err && (
        <div ref={errRef} role="alert" className="ts-err" style={{ margin: '10px 0' }}>
          {err}
        </div>
      )}

      {stage === 'pick' && (
        <>
          {sheetPicker}
          <p className="tr-lede">
            Bring the tests you kept in a spreadsheet into this tank's history. One row per test
            day, a Date column and one column per test (Alk, Calcium, Nitrate…). A sheet with Date,
            Parameter and Value columns works too. You check every column and unit before anything
            is saved. A test newer than your latest logged one is not imported as history: you can
            save it as a test instead, so it is checked like one you log by hand.
          </p>
          <label className="ir-file tr-btn tr-btn-primary font-heading">
            {busy ? 'Reading…' : 'Choose a file (CSV or Excel)'}
            <input
              type="file"
              accept={FILE_TYPES}
              disabled={busy}
              onChange={(e) => {
                readFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          <label className="ir-field">
            <span className="ir-label font-heading">
              Or paste from Google Sheets, Excel or Numbers
            </span>
            <textarea
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              rows={5}
              placeholder={'Date\tAlk\tCalcium\n03/10/2026\t8.2\t430'}
              spellCheck={false}
            />
          </label>
          <button
            type="button"
            className="tr-btn font-heading"
            disabled={!paste.trim() || busy}
            onClick={readPaste}
          >
            Read the pasted rows
          </button>
        </>
      )}

      {stage === 'map' && table && (
        <>
          <p className="tr-lede">
            {fileName}: {table.length - 1} row{table.length === 2 ? '' : 's'}
            {headerLine > 1 ? `, headings on row ${headerLine}` : ''}
            {book ? `, sheet "${book.sheets[book.sheet] ?? ''}"` : ''}. Check what each column is
            and its unit. Columns set to Skip are left out.
          </p>
          {sheetPicker}
          {detectedOrder === 'ambiguous' && (
            <div className="ir-card" role="group" aria-label="Date order">
              <div className="ir-q">
                Every date here could read two ways. Is {sampleDate || '03/04/2026'}…
              </div>
              <div className="tr-actions" style={{ margin: '8px 0 0' }}>
                {[
                  ['dmy', 'Day first'],
                  ['mdy', 'Month first'],
                ].map(([k, lead]) => {
                  const d = parseDateCell(sampleDate || '03/04/2026', k);
                  const text = d
                    ? `${lead} (${d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })})`
                    : lead;
                  return (
                    <button
                      key={k}
                      type="button"
                      className={`tr-btn font-heading ${dateOrder === k ? 'tr-btn-primary' : ''}`}
                      aria-pressed={dateOrder === k}
                      onClick={() => {
                        setCheck(null);
                        setIncludeOdd(false);
                        setDateOrder(k);
                      }}
                    >
                      {text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {detectedOrder === 'mixed' && (
            <div className="ts-err" role="alert" style={{ margin: '10px 0' }}>
              Some dates read day first and some month first, so they cannot all be right. Fix the
              dates in the sheet and try again.
            </div>
          )}
          <div className="ir-cols">
            {columns.map((c, i) => {
              const samples = table
                .slice(1)
                .map((r) => r[i])
                .filter((v) => String(v ?? '').trim() !== '')
                .slice(0, 3);
              const value =
                c.kind === 'date'
                  ? '__date'
                  : c.kind === 'time'
                    ? '__time'
                    : c.kind === 'param'
                      ? c.param
                      : '__skip';
              const units = c.kind === 'param' ? UNIT_OPTIONS[c.param] : null;
              return (
                <div key={i} className={`ir-col ${c.kind === 'skip' ? 'is-skip' : ''}`}>
                  <div className="ir-col-head">
                    <span className="ir-col-name">{c.header || `Column ${i + 1}`}</span>
                    <span className="ir-col-sample font-mono">
                      {samples.join(' · ') || 'empty'}
                    </span>
                  </div>
                  <select
                    aria-label={`What is ${c.header || `column ${i + 1}`}?`}
                    value={value}
                    onChange={(e) => setColumn(i, e.target.value)}
                  >
                    <option value="__skip">Skip</option>
                    <option value="__date">Date</option>
                    <option value="__time">Time of day</option>
                    {params.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  {units && units.length > 1 && (
                    <select
                      aria-label={`Unit for ${c.header}`}
                      value={c.unit || units[0].k}
                      onChange={(e) => setUnit(i, e.target.value)}
                    >
                      {units.map((u) => (
                        <option key={u.k} value={u.k}>
                          In {u.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {c.kind === 'skip' && matchParam(c.header, hiddenMap) && (
                    <div className="ir-note">
                      {label(matchParam(c.header, hiddenMap))} is hidden on this tank, so this
                      column is left out.
                    </div>
                  )}
                  {c.kind === 'param' && unitPreview(c, samples) && (
                    <div className="ir-note font-mono">{unitPreview(c, samples)}</div>
                  )}
                  {c.kind === 'param' && c.param === 'phosphate' && c.unit === 'ppb' && (
                    <div className="ir-note">
                      Hanna's Phosphorus checker reads ppb of phosphorus, not phosphate. If these
                      numbers came from it, choose ppb phosphorus.
                    </div>
                  )}
                  {c.kind === 'param' && c.param === 'salinity' && (
                    <div className="ir-note">ppt, PSU or S.G.: each number is read on its own.</div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="ir-note" style={{ margin: '4px 0 12px' }}>
            A test this tank has no card for (in Parameters on Home) cannot be imported. Add the
            card first, then come back.
          </p>
          <div className="tr-actions">
            <button
              type="button"
              className="tr-btn tr-btn-primary font-heading"
              disabled={busy || detectedOrder === 'mixed'}
              onClick={() => runCheck()}
            >
              {busy ? 'Checking…' : 'Check the readings'}
            </button>
            <button type="button" className="tr-btn font-heading" onClick={reset}>
              Start again
            </button>
          </div>
        </>
      )}

      {stage === 'check' && check && built && (
        <>
          <div className="ir-card">
            <div className="ir-big font-heading">
              {check.ready} reading{check.ready === 1 ? '' : 's'} ready to import
            </div>
            {check.duplicates > 0 && (
              <div className="ir-note">
                {check.duplicates} already in this tank (same test, same value, same day), left out.
              </div>
            )}
            <ul className="ir-sum">
              {Object.entries(perParam).map(([k, e]) => (
                <li key={k}>
                  <strong>{label(k)}</strong>: {e.n} from {fmtDay(e.first)} to {fmtDay(e.last)}.
                  {e.n > 1 && e.min != null && e.min !== e.max
                    ? ` Lowest ${show(k, e.min)}, highest ${endSentence(show(k, e.max))}`
                    : ''}{' '}
                  Latest {endSentence(show(k, e.last_value))}
                </li>
              ))}
            </ul>
            <div className="ir-note">
              Imported readings show on the charts and in each test's history, marked IMPORTED. They
              send no alerts and tick off no tasks. You can remove the whole import later.
            </div>
          </div>
          {recentNewest.length > 0 && (
            <div className="ir-card">
              <div className="ir-q">Recent tests</div>
              <div className="ir-note">
                Newer than your latest logged test, so they would be your current reading. Save the
                newest of each as a test: it is checked like a test you log by hand and can send an
                alert. Older ones then import as history.
              </div>
              <ul className="ir-recent">
                {recentNewest.map((r) => (
                  <li key={r.param}>
                    <span>
                      <strong>{label(r.param)}</strong> {show(r.param, r.value)}, {whenTested(r)}
                    </span>
                    <button
                      type="button"
                      className="tr-btn font-heading"
                      disabled={busy}
                      onClick={() => saveAsTest(r)}
                    >
                      Save as a test
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {odd.length > 0 && (
            <div className="ir-card">
              <div className="ir-q">
                Check {odd.length === 1 ? 'this number' : `these ${odd.length} numbers`}
              </div>
              <div className="ir-note">
                Far outside your range for that test, often a typo, a decimal point in the wrong
                place or the wrong unit.{' '}
                {includeOdd
                  ? 'They are in the import.'
                  : 'They are left out unless you say they are right.'}
              </div>
              <ul className="ir-refused">
                {odd.slice(0, 50).map((r, i) => (
                  <li key={i}>
                    Row {r.line ?? '?'}, {label(r.param)}: {show(r.param, r.value)}
                  </li>
                ))}
                {odd.length > 50 && <li>and {odd.length - 50} more.</li>}
              </ul>
              <label className="ir-check">
                <input
                  type="checkbox"
                  checked={includeOdd}
                  disabled={busy}
                  onChange={async (e) => {
                    const want = e.target.checked;
                    setIncludeOdd(want);
                    // A failed re-check puts the box back, so the count on
                    // the Import button always matches what is sent.
                    if (!(await runCheck(want))) setIncludeOdd(!want);
                  }}
                />
                <span>They are right, import them too</span>
              </label>
            </div>
          )}
          {refusedAll.length > 0 && (
            <details className="ir-card" open={refusedAll.length <= 8}>
              <summary className="ir-q">
                {refusedAll.length} cell{refusedAll.length === 1 ? '' : 's'} left out
              </summary>
              <ul className="ir-refused">
                {refusedAll.slice(0, 200).map((r, i) => (
                  <li key={i}>
                    Row {r.line ?? '?'}
                    {r.header ? `, ${r.header}` : r.param ? `, ${label(r.param)}` : ''}
                    {r.cell != null ? ` (${r.cell})` : ''}: {r.reason}
                  </li>
                ))}
                {refusedAll.length > 200 && <li>and {refusedAll.length - 200} more.</li>}
              </ul>
            </details>
          )}
          <div className="tr-actions">
            <button
              type="button"
              className="tr-btn tr-btn-primary font-heading"
              disabled={busy || !check.ready}
              onClick={runImport}
            >
              {busy ? 'Importing…' : `Import ${check.ready} reading${check.ready === 1 ? '' : 's'}`}
            </button>
            <button type="button" className="tr-btn font-heading" onClick={() => setStage('map')}>
              Back to the columns
            </button>
          </div>
        </>
      )}

      {stage === 'done' && result && (
        <div className="ir-card">
          <div className="ir-big font-heading">
            Imported {result.saved} reading{result.saved === 1 ? '' : 's'}
          </div>
          <div className="ir-note">
            From {fmtDay(result.first_ts)} to {fmtDay(result.last_ts)}. They are on the charts now.
            {!includeOdd && odd.length > 0
              ? ` ${odd.length} number${odd.length === 1 ? '' : 's'} far outside your range ${odd.length === 1 ? 'was' : 'were'} left out.`
              : ''}
          </div>
          <div className="tr-actions">
            {onOpenAllHistory && (
              <button
                type="button"
                className="tr-btn tr-btn-primary font-heading"
                onClick={() => onOpenAllHistory(result.first_ts)}
              >
                See all history
              </button>
            )}
            <button type="button" className="tr-btn font-heading" onClick={reset}>
              Import another
            </button>
          </div>
        </div>
      )}

      {imports && imports.length > 0 && (
        <section style={{ marginTop: 24 }}>
          <h3 className="font-heading ir-h3">Past imports</h3>
          <ul className="ir-past">
            {imports.map((imp) => (
              <li key={imp.import_id} className="ir-card">
                <div className="ir-past-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="ir-past-file">{imp.file}</div>
                    <div className="ir-note">
                      {imp.count} reading{imp.count === 1 ? '' : 's'}, {fmtDay(imp.first_ts)} to{' '}
                      {fmtDay(imp.last_ts)}. Imported {fmtDay(imp.at)}
                      {imp.mine ? '' : ' by someone you share the tank with'}.
                    </div>
                  </div>
                  {imp.can_remove && (
                    <button
                      type="button"
                      className="tr-btn font-heading ir-remove"
                      onClick={() => removeImport(imp)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
